import React, { FC, useEffect, useState } from "react";
import { DefaultButton, mergeStyles, mergeStyleSets } from "@fluentui/react";
import { MessageBar } from "@fluentui/react";
import { MessageBarType } from "@fluentui/react";
import { Nomination, PatchModel } from "../../models";
import { Patch } from "../../api/apiUtils";
import SofIcon from "../common/Icons";
import { FontIcon } from "@fluentui/react";
import clsx from "clsx";
import { stringify, trimEmpty } from "../../services/Helper";

type Props = Nomination & {
  lineHandlingStatus: number;
  onSuccess(result: Nomination): void;
  onClose(): void;
};

const button = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  flex: "1 1 auto",
  marginLeft: 5,
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const table = mergeStyles({
  background: "#FAFAFA",
  overflow: "hidden"
});

const classes = mergeStyleSets({
  container: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    top: 188,
    bottom: 0,
    left: 12,
    width: "34%",
    background: "#FFFFFF",
    zIndex: 1,
    border: "1px solid #E2E6E8"
  },
  headerContainer: {
    padding: "15px 10px",
    backgroundColor: "var(--primary-500)",
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold"
  },
  bodyContainer: {
    padding: "15px 10px",
    flex: "1 1 auto",
    height: 0,
    overflowY: "auto"
  },
  detailSwitch: {
    padding: "0 0 10px 0",
    color: "#4C4C4C",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    selectors: {
      [":active"]: {
        backgroundColor: "#008cc0"
      }
    }
  },
  detailSwitchLabel: {
    flex: "1 1 auto"
  },
  footerContainer: {
    display: "flex",
    padding: 10
  },
  fieldLabel: {
    color: "#7C7777",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 20,
    display: "block",
    marginTop: 12
  },
  fieldInputBox: {
    border: "1px solid #D8DFE6",
    width: "100%",
    padding: "5px 10px",
    selectors: {
      [":focus"]: {
        border: "1px solid #008cc0"
      }
    }
  },
  customInput: {
    border: "1px solid #D8DFE6",
    width: "100%",
    display: "flex"
  },
  customTextInput: {
    border: "none",
    flex: "1 1 auto",
    padding: "5px 10px"
  },
  customInputRight: {
    borderLeft: "1px solid #E2E6E8",
    padding: "6px 10px 0 10px",
    lineHeight: 1,
    backgroundColor: "#EEEEEE",
    fontSize: 14,
    color: "#4C4C4C"
  },
  submitButton: [
    button,
    {
      backgroundColor: "var(--primary-500)",
      color: "#FFFFFF",
      margin: "0 5px 0 0",
      selectors: {
        [":disabled"]: {
          color: "#7C7777",
          backgroundColor: "#B8B8B8"
        }
      }
    }
  ]
});

const NominationForm: FC<Props> = (props: Props) => {
  const [shipLine, setShipLine] = useState<string>();
  const [vaporLine, setVaporLine] = useState<string>();
  const [shipVaporLine, setShipVaporLine] = useState<string>();
  const [shipVaporLineDescription, setShipVaporLineDescription] =
    useState<string>();
  const [stowage, setStowage] = useState<string>();
  const [showDetails, setShowDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const shipVaporLines: string[] = ["N/A", "N2 Reg", "Vapor Line"];

  const onSave = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      setIsLoading(true);
      setErrorMessage(undefined);

      const payload: PatchModel[] = [
        {
          op: "replace",
          path: "/shipLine",
          value: shipLine
        },
        {
          op: "replace",
          path: "/shipVaporLine",
          value: shipVaporLine
        },
        {
          op: "replace",
          path: "/shipVaporLineDescription",
          value: shipVaporLineDescription
        },
        {
          op: "replace",
          path: "/vaporLine",
          value: vaporLine
        },
        {
          op: "replace",
          path: "/stowage",
          value: stowage
        }
      ];
      const response = await Patch(
        `/api/nomination/${props.nominationNo}`,
        JSON.stringify(payload)
      );
      setIsLoading(false);

      if (response.ok) {
        const result = (await response.json()) as Nomination;
        props.onSuccess(result);
      } else {
        setErrorMessage("Unable to save changes");
      }
    } catch {
      setErrorMessage("Unable to save changes. Please try to reconnect.");

      setIsLoading(false);
    }
  };

  const getStyle = () => {
    if (showDetails) return mergeStyles([table]);
    else return mergeStyles([table], { height: 1 });
  };

  useEffect(() => {
    setShipLine(props.shipLine);
    setVaporLine(props.vaporLine);
    setShipVaporLine(props.shipVaporLine);
    setShipVaporLineDescription(props.shipVaporLineDescription);
    setStowage(props.stowage);
  }, [
    props.shipLine,
    props.vaporLine,
    props.shipVaporLine,
    props.shipVaporLineDescription,
    props.stowage
  ]);

  useEffect(() => {
    setHasChanges(
      stringify(shipLine) !== stringify(props.shipLine) ||
        stringify(shipVaporLine) !== stringify(props.shipVaporLine) ||
        stringify(shipVaporLineDescription) !==
          stringify(props.shipVaporLineDescription) ||
        stringify(vaporLine) !== stringify(props.vaporLine) ||
        stringify(stowage) !== stringify(props.stowage)
    );
  }, [shipLine, shipVaporLine, shipVaporLineDescription, vaporLine, stowage]);

  return (
    <form className={classes.container} onSubmit={onSave}>
      <div id="lbl-edit-nomination" className={classes.headerContainer}>
        Edit - Nomination#&nbsp;
        {props.nominationNo.substr(props.nominationNo.length - 6)}
      </div>
      <div className={clsx(classes.bodyContainer, "custom-scroll")}>
        <div
          id="btn-toggle-nomination-detail"
          className={classes.detailSwitch}
          onClick={() => setShowDetails(!showDetails)}
        >
          <span className={classes.detailSwitchLabel}>Nomination Details</span>
          <SofIcon iconname={showDetails ? "ArrowUpLg" : "ArrowDownLg"} />
        </div>
        <div className={getStyle()}>
          <table className={clsx("tbl-detail")}>
            <tbody>
              <tr>
                <td colSpan={2}>
                  <dt>Surveyor</dt>
                  <dd id="lbl-nomination-surveyor">{props.surveyor}&nbsp;</dd>
                </td>
              </tr>
              <tr>
                <td>
                  <dt>Direction</dt>
                  <dd id="lbl-nomination-direction">
                    {props.nominationType}&nbsp;
                  </dd>
                </td>
                <td>
                  <dt>Dock</dt>
                  <dd id="lbl-nomination-dock">{props.dock}&nbsp;</dd>
                </td>
              </tr>
              <tr>
                <td>
                  <dt>Customer</dt>
                  <dd id="lbl-nomination-customer">{props.customer}&nbsp;</dd>
                </td>
                <td>
                  <dt>Product</dt>
                  <dd id="lbl-nomination-product">{props.product}&nbsp;</dd>
                </td>
              </tr>
              <tr>
                <td>
                  <dt>
                    {props.transferType === "OTS Transfer" ? "Vessel" : "Tank"}
                  </dt>
                  <dd
                    id={`lbl-nomination-${
                      props.transferType === "OTS Transfer" ? "vessel" : "tank"
                    }`}
                  >
                    {props.transferType === "Tank Transfer"
                      ? props.tank.substring(4)
                      : ""}
                    &nbsp;
                  </dd>
                </td>
                <td>
                  <dt>Quantity</dt>
                  <dd id="lbl-nomination-surveyor">{props.quantity}</dd>
                </td>
              </tr>
              {props.transferType !== "OTS Transfer" && (
                <tr>
                  <td>
                    <dt>Jetty Line</dt>
                    <dd id="lbl-nomination-surveyor">
                      {props.jettyLine}&nbsp;
                    </dd>
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {props.transferType !== "OTS Transfer" && (
          <div>
            <label htmlFor="txt-vapor-line" className={classes.fieldLabel}>
              Vapor Line
            </label>
            <input
              id="txt-vapor-line"
              className={classes.fieldInputBox}
              value={vaporLine}
              onChange={(e) => setVaporLine(trimEmpty(e.target.value))}
              onBlur={(e) => setVaporLine(e.target.value.trim())}
              disabled={isLoading}
              autoFocus={props.transferType !== "OTS Transfer"}
              maxLength={150}
            />
          </div>
        )}

        <div>
          <label htmlFor="txt-ship-line" className={classes.fieldLabel}>
            Ship Line
          </label>
          <input
            id="txt-ship-line"
            className={classes.fieldInputBox}
            value={shipLine}
            onChange={(e) => setShipLine(trimEmpty(e.target.value))}
            onBlur={(e) => setShipLine(e.target.value.trim())}
            disabled={isLoading}
            autoFocus={props.transferType !== "OTS Transfer" ? false : true}
            maxLength={150}
          />
        </div>

        <div>
          <label htmlFor="txt-ship-vapor-line" className={classes.fieldLabel}>
            Ship Vapor Line
          </label>

          {(props.lineHandlingStatus < 2 || !props.shipVaporLine) && (
            <div className="btn-menu-group">
              {shipVaporLines.map((svl) => (
                <div
                  key={svl}
                  className={clsx(
                    "btn-menu",
                    shipVaporLine === svl && "btn-menu-selected"
                  )}
                  onClick={() => setShipVaporLine(svl)}
                  style={{ marginLeft: "2px", marginRight: "2px" }}
                >
                  <FontIcon
                    iconName={
                      shipVaporLine === svl ? "RadioBtnOn" : "RadioBtnOff"
                    }
                    style={{ fontSize: "12px" }}
                  />
                  &nbsp;
                  {svl}
                </div>
              ))}
            </div>
          )}

          {props.lineHandlingStatus >= 2 && props.shipVaporLine && (
            <div className="btn-menu-group">
              {shipVaporLines.map((svl) => (
                <div
                  className="btn-menu btn-menu-disabled"
                  style={{ marginLeft: "2px", marginRight: "2px" }}
                >
                  <FontIcon
                    iconName={
                      shipVaporLine === svl ? "RadioBtnOn" : "RadioBtnOff"
                    }
                    style={{ fontSize: "12px" }}
                  />
                  &nbsp;
                  {svl}
                </div>
              ))}
            </div>
          )}
        </div>

        {shipVaporLine === "Vapor Line" && (
          <div>
            <label
              htmlFor="txt-ship-vapor-line-description"
              className={classes.fieldLabel}
            >
              Ship Vapor Line Description
            </label>
            <input
              id="txt-ship-vapor-line-description"
              className={classes.fieldInputBox}
              value={shipVaporLineDescription}
              onChange={(e) =>
                setShipVaporLineDescription(
                  `${
                    shipVaporLine === "Vapor Line"
                      ? trimEmpty(e.target.value)
                      : ""
                  }`
                )
              }
              onBlur={(e) => setShipVaporLineDescription(e.target.value.trim())}
              disabled={isLoading}
              maxLength={150}
            />
          </div>
        )}

        <div>
          <label htmlFor="txt-stowage" className={classes.fieldLabel}>
            Stowage
          </label>
          <input
            id="txt-stowage"
            className={classes.fieldInputBox}
            value={stowage}
            onChange={(e) => setStowage(trimEmpty(e.target.value))}
            onBlur={(e) => setStowage(e.target.value.trim())}
            disabled={isLoading}
            maxLength={150}
          />
        </div>

        {errorMessage && (
          <div style={{ marginTop: "10px" }}>
            <MessageBar
              messageBarType={MessageBarType.error}
              onDismiss={() => setErrorMessage(undefined)}
            >
              {errorMessage}
            </MessageBar>
          </div>
        )}
      </div>
          <div className={classes.footerContainer}>

        <button
          id="btn-save-nomination"
          className={classes.submitButton}
          type="submit"
          disabled={!hasChanges || isLoading}
        >
          {isLoading ? "Saving.." : "Save"}
              </button>

              <DefaultButton
                  id="btn-cancel-nomination"
                  onClick={props.onClose}
                  disabled={isLoading}
                  text="Cancel"
              />
        {/*<button*/}
        {/*  id="btn-cancel-nomination"*/}
        {/*  className={button}*/}
        {/*  onClick={props.onClose}*/}
        {/*  disabled={isLoading}*/}
        {/*>*/}
        {/*  Cancel*/}
        {/*</button>*/}
      </div>
    </form>
  );
};

export default NominationForm;
